import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { QuotationLineItems } from "./QuotationLineItems";
import { QuotationTotals } from "./QuotationTotals";
import { QuotationPreview } from "./QuotationPreview";
import { FullPreviewModal } from "./FullPreviewModal";
import { SendQuotationModal } from "./SendQuotationModal";
import { SaveAsTemplateModal } from "./SaveAsTemplateModal";
import { TemplatePickerModal } from "./TemplatePickerModal";
import { defaultLineItems, defaultTerms, computeSubtotal, computeTaxTotal, computeTotal, companyInfo as fallbackCompanyInfo } from "@/mocks/quotationsData";
import type { QuotationLineItem } from "@/mocks/quotationsData";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";

export interface QuotationLeadItem {
  id: number | string;
  name: string;
  company?: string;
  phone?: string;
  email?: string;
}

type Props = {
  mode: "create" | "edit";
  quotationId?: string | number;
  initialData?: {
    quoteNo: string;
    date: string;
    validUntil: string;
    lead: QuotationLeadItem | null;
    agent: string;
    terms: string;
    items: QuotationLineItem[];
    discount: number;
  };
  preselectedLeadId?: number | string | null;
  initialLead?: QuotationLeadItem | null;
};

export function QuotationBuilder({ mode, quotationId, initialData, preselectedLeadId, initialLead }: Props) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const getToday = () => new Date().toISOString().split("T")[0];
  const get30DaysLater = () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split("T")[0];
  };

  // State
  const [quoteNo, setQuoteNo] = useState(
    initialData?.quoteNo || `QT-${Math.floor(1000 + Math.random() * 9000)}`
  );
  const [date, setDate] = useState(initialData?.date || getToday());
  const [validUntil, setValidUntil] = useState(initialData?.validUntil || get30DaysLater());
  const [lead, setLead] = useState<QuotationLeadItem | null>(initialData?.lead || initialLead || null);
  const [agent, setAgent] = useState(initialData?.agent || "");
  const [agentsList, setAgentsList] = useState<string[]>([]);
  const [dbLeads, setDbLeads] = useState<QuotationLeadItem[]>([]);
  const [terms, setTerms] = useState(initialData?.terms || defaultTerms);
  const [items, setItems] = useState<QuotationLineItem[]>(initialData?.items || defaultLineItems);
  const [discount, setDiscount] = useState(initialData?.discount || 0);
  const [leadSearchOpen, setLeadSearchOpen] = useState(false);
  const [leadSearch, setLeadSearch] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(fallbackCompanyInfo);

  // 1. Fetch Dynamic Agents from Backend
  useEffect(() => {
    fetchApi<any>("/auth/company-user/all", undefined, null)
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const names: string[] = [];
        userList.forEach((u: any) => {
          if (u.name) names.push(u.name);
          else if (u.fullName) names.push(u.fullName);
          else if (u.username) names.push(u.username);
          if (u.leader_name) names.push(u.leader_name);
          if (Array.isArray(u.member_names)) names.push(...u.member_names);
        });

        const unique = Array.from(new Set(names)).filter(Boolean);
        if (unique.length > 0) {
          setAgentsList(unique);
          if (!agent && !initialData?.agent) {
            setAgent(unique[0]);
          }
        } else {
          setAgentsList(["Admin", "Rajesh Kumar", "Priya Sharma", "Amit Patel", "Sneha Rao"]);
          if (!agent && !initialData?.agent) setAgent("Admin");
        }
      })
      .catch(() => {
        setAgentsList(["Admin", "Rajesh Kumar", "Priya Sharma", "Amit Patel", "Sneha Rao"]);
        if (!agent && !initialData?.agent) setAgent("Admin");
      });
  }, [initialData?.agent, agent]);

  // 2. Fetch Dynamic Leads from Backend
  useEffect(() => {
    fetchApi<any>("/leads?limit=200", undefined, null)
      .then((res) => {
        const rawList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const mapped: QuotationLeadItem[] = rawList.map((l: any) => ({
          id: l.id,
          name: l.name || "Unnamed Lead",
          company: l.company || "",
          phone: l.phone || "",
          email: l.email || "",
        }));

        setDbLeads(mapped);

        // If preselectedLeadId is given and lead is not set yet
        if (preselectedLeadId && !lead) {
          const match = mapped.find((l) => String(l.id) === String(preselectedLeadId));
          if (match) {
            setLead(match);
          } else {
            // Fetch single lead
            fetchApi<any>(`/leads/${preselectedLeadId}`, undefined, null)
              .then((singleLead) => {
                if (singleLead) {
                  const leadObj: QuotationLeadItem = {
                    id: singleLead.id,
                    name: singleLead.name,
                    company: singleLead.company || "",
                    phone: singleLead.phone || "",
                    email: singleLead.email || "",
                  };
                  setLead(leadObj);
                }
              })
              .catch(() => {});
          }
        }
      })
      .catch(() => {});
  }, [preselectedLeadId, lead]);

  // 3. Fetch Company Profile
  useEffect(() => {
    fetchApi<any>("/company", undefined, null)
      .then((res) => {
        const c = Array.isArray(res?.data) ? res.data[0] : res?.data || res;
        if (c?.name) {
          setCompanyDetails((prev) => ({
            ...prev,
            name: c.name || prev.name,
            address: c.address || prev.address,
            phone: c.phone || prev.phone,
            email: c.email || prev.email,
            gstin: c.gst_number || c.gstin || prev.gstin,
          }));
        }
      })
      .catch(() => {});
  }, []);

  // Filter leads based on search
  const filteredLeads = leadSearch.trim()
    ? dbLeads.filter(
        (l) =>
          l.name.toLowerCase().includes(leadSearch.toLowerCase()) ||
          (l.company && l.company.toLowerCase().includes(leadSearch.toLowerCase())) ||
          (l.phone && l.phone.includes(leadSearch))
      )
    : dbLeads;

  const subtotal = computeSubtotal(items);
  const taxTotal = computeTaxTotal(items);
  const total = computeTotal(items, discount);
  const totalFormatted = total >= 100000 ? "₹" + (total / 100000).toFixed(1) + "L" : "₹" + total.toLocaleString("en-IN");

  // Save Quotation to Backend
  const handleSave = useCallback(
    async (targetStatus = "Draft") => {
      if (!lead && !initialData?.lead) {
        showToast("Please select a lead or client for this quotation", "error");
        return;
      }

      setSaving(true);
      try {
        const payload = {
          quote_number: quoteNo,
          lead_id: lead?.id || null,
          client_name: lead?.name || "Client",
          client_company: lead?.company || "",
          client_email: lead?.email || "",
          client_phone: lead?.phone || "",
          agent: agent || "Unassigned",
          quote_date: date,
          valid_until: validUntil,
          status: targetStatus,
          discount,
          tax: taxTotal,
          total_amount: total,
          items,
          terms,
        };

        if (mode === "create") {
          await fetchApi("/quotations", {
            method: "POST",
            body: JSON.stringify(payload),
          });
          showToast("Quotation created successfully", "success");
        } else {
          const targetId = quotationId || quoteNo;
          await fetchApi(`/quotations/${targetId}`, {
            method: "PUT",
            body: JSON.stringify(payload),
          });
          showToast("Quotation updated successfully", "success");
        }
        navigate("/quotations");
      } catch (err: any) {
        showToast(err.message || "Failed to save quotation", "error");
      } finally {
        setSaving(false);
      }
    },
    [lead, initialData?.lead, quoteNo, agent, date, validUntil, discount, taxTotal, total, items, terms, mode, quotationId, navigate, showToast]
  );

  const handleDownload = useCallback(() => {
    showToast("Generating PDF...", "info");
    const container = document.createElement("div");
    container.className = "quotation-print-area";
    container.style.cssText = "display:none;";
    const subtotalForPrint = computeSubtotal(items);
    const taxForPrint = computeTaxTotal(items);
    const totalForPrint = computeTotal(items, discount);
    container.innerHTML = generatePrintHtml(
      quoteNo,
      lead?.name || "",
      lead?.company || "",
      date,
      validUntil,
      items,
      discount,
      terms,
      agent,
      subtotalForPrint,
      taxForPrint,
      totalForPrint,
      companyDetails
    );
    document.body.appendChild(container);
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.body.removeChild(container);
      }, 500);
    }, 100);
  }, [showToast, quoteNo, lead, date, validUntil, items, discount, terms, agent, companyDetails]);

  const handleApplyTemplate = useCallback((templateItems: QuotationLineItem[]) => {
    setItems(templateItems);
  }, []);

  return (
    <CrmLayout title={mode === "create" ? "New Quotation" : "Edit Quotation"}>
      <div className="p-5 md:p-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
          <button onClick={() => navigate("/quotations")} className="hover:text-indigo-600 transition-colors cursor-pointer">
            Quotations
          </button>
          <i className="ri-arrow-right-s-line text-gray-300" />
          <span className="text-indigo-600 font-medium">{mode === "create" ? "New Quotation" : "Edit Quotation"}</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-5">
          {/* LEFT — Builder */}
          <div className="w-full lg:w-[65%] space-y-4">
            {/* Section 1: Quotation Details */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Quotation Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Quote Number</label>
                  <input
                    type="text"
                    value={quoteNo}
                    onChange={(e) => setQuoteNo(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-700 outline-none focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Valid Until</label>
                  <input
                    type="date"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Assigned Agent</label>
                  <select
                    value={agent}
                    onChange={(e) => setAgent(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all cursor-pointer"
                  >
                    {agentsList.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Lead searchable dropdown */}
              <div className="mt-4 relative">
                <label className="block text-xs font-medium text-gray-500 mb-1">Lead / Client</label>
                {lead ? (
                  <div className="flex items-center gap-3 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-indigo-600">
                        {lead.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{lead.name}</p>
                      <p className="text-xs text-gray-500">
                        {lead.company || "Individual"} {lead.phone ? `· ${lead.phone}` : ""} {lead.email ? `· ${lead.email}` : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setLead(null);
                        setLeadSearch("");
                      }}
                      className="text-gray-400 hover:text-gray-600 cursor-pointer p-1"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      value={leadSearch}
                      onChange={(e) => {
                        setLeadSearch(e.target.value);
                        setLeadSearchOpen(true);
                      }}
                      onFocus={() => setLeadSearchOpen(true)}
                      placeholder="Search leads by name, company, or phone..."
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all"
                    />
                    {leadSearchOpen && (
                      <>
                        <div className="fixed inset-0 z-20" onClick={() => setLeadSearchOpen(false)} />
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 max-h-52 overflow-y-auto">
                          {filteredLeads.length === 0 ? (
                            <p className="px-4 py-3 text-sm text-gray-400">No matching leads found</p>
                          ) : (
                            filteredLeads.map((l) => (
                              <button
                                key={l.id}
                                onClick={() => {
                                  setLead(l);
                                  setLeadSearch("");
                                  setLeadSearchOpen(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors cursor-pointer text-left"
                              >
                                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-bold text-indigo-600">
                                    {l.name
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")
                                      .toUpperCase()}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900">{l.name}</p>
                                  <p className="text-xs text-gray-500">
                                    {l.company || "Individual"} {l.phone ? `· ${l.phone}` : ""}
                                  </p>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Terms & Notes */}
              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">Payment Terms &amp; Notes</label>
                <textarea
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  placeholder="Payment Terms, special conditions..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all resize-none"
                />
              </div>
            </div>

            {/* Section 2: Company Info */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">From (Your Company)</h3>
                <button
                  onClick={() => navigate("/settings/company")}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-700 cursor-pointer"
                >
                  Edit
                </button>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <span className="text-xs text-gray-400">Company</span>
                  <p className="font-medium text-gray-900">{companyDetails.name}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">GSTIN</span>
                  <p className="font-medium text-gray-900">{companyDetails.gstin}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Address</span>
                  <p className="font-medium text-gray-900">{companyDetails.address}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Phone</span>
                  <p className="font-medium text-gray-900">{companyDetails.phone}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Email</span>
                  <p className="font-medium text-gray-900">{companyDetails.email}</p>
                </div>
              </div>
            </div>

            {/* Section 3: Line Items */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Items &amp; Services</h3>
                <button
                  onClick={() => setTemplatePickerOpen(true)}
                  className="px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-copy-line mr-1" />
                  Use Template
                </button>
              </div>
              <QuotationLineItems items={items} onChange={setItems} />
            </div>

            {/* Section 4: Totals */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <QuotationTotals items={items} discount={discount} onDiscountChange={setDiscount} />
            </div>

            {/* Section 5: Terms & Conditions */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Terms &amp; Conditions</h3>
              <textarea
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none transition-all resize-none"
              />
            </div>
          </div>

          {/* RIGHT — Preview */}
          <div className="w-full lg:w-[35%]">
            <div className="sticky top-20 space-y-4">
              <QuotationPreview
                quoteNo={quoteNo}
                date={date}
                validUntil={validUntil}
                leadName={lead?.name || ""}
                leadPhone={lead?.phone || ""}
                leadEmail={lead?.email || ""}
                items={items}
                discount={discount}
                terms={terms}
                agent={agent}
                companyInfo={companyDetails}
              />
              <button
                onClick={() => setPreviewOpen(true)}
                className="w-full px-4 py-2.5 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-eye-line mr-1.5" />
                Full Preview
              </button>
            </div>
          </div>
        </div>

        {/* Sticky Bottom Action Bar */}
        <div className="sticky bottom-0 mt-6 -mx-5 md:-mx-6 px-5 md:px-6 py-4 bg-white border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 rounded-b-lg z-20">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSave("Draft")}
              disabled={saving}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              {saving ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-save-line" />}
              Save as Draft
            </button>
            <button
              onClick={() => setSaveTemplateOpen(true)}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-copy-line mr-1.5" />
              Save as Template
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreviewOpen(true)}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-eye-line mr-1.5" />
              Preview
            </button>
            <button
              onClick={() => setSendModalOpen(true)}
              className="px-4 py-2.5 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-send-plane-line mr-1.5" />
              Send to Lead
            </button>
            <button
              onClick={handleDownload}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-line mr-1.5" />
              Download PDF
            </button>
          </div>
        </div>

        {/* Full Preview Modal */}
        <FullPreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          quoteNo={quoteNo}
          date={date}
          validUntil={validUntil}
          leadName={lead?.name || ""}
          leadPhone={lead?.phone || ""}
          leadEmail={lead?.email || ""}
          leadCompany={lead?.company || ""}
          items={items}
          discount={discount}
          terms={terms}
          agent={agent}
          companyInfo={companyDetails}
        />

        {/* Send Quotation Modal */}
        <SendQuotationModal
          open={sendModalOpen}
          onClose={() => setSendModalOpen(false)}
          quoteId={quotationId}
          quoteNo={quoteNo}
          leadName={lead?.name || ""}
          leadEmail={lead?.email || ""}
          totalFormatted={totalFormatted}
          validUntil={validUntil}
          agent={agent}
          companyName={companyDetails.name}
          leadId={lead?.id}
        />

        {/* Save as Template Modal */}
        <SaveAsTemplateModal
          open={saveTemplateOpen}
          onClose={() => setSaveTemplateOpen(false)}
          items={items}
          createdBy={agent || "Admin"}
        />

        {/* Template Picker Modal */}
        <TemplatePickerModal
          open={templatePickerOpen}
          onClose={() => setTemplatePickerOpen(false)}
          onApplyTemplate={handleApplyTemplate}
        />
      </div>
    </CrmLayout>
  );
}

function generatePrintHtml(
  quoteNo: string,
  leadName: string,
  leadCompany: string,
  date: string,
  validUntil: string,
  items: QuotationLineItem[],
  discount: number,
  terms: string,
  agent: string,
  subtotal: number,
  taxTotal: number,
  total: number,
  company: { name: string; address: string; phone: string; email: string; gstin: string }
) {
  const fmtDate = (d: string) => {
    if (!d || d === "—") return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };
  const fmtCurr = (v: number) => "₹" + Number(v || 0).toLocaleString("en-IN");
  const itemsHtml = items
    .map((item, idx) => {
      const amt = (Number(item.qty) || 0) * (Number(item.rate) || 0) * (1 + (Number(item.taxPercent) || 0) / 100);
      return `<tr><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;">${idx + 1}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;font-weight:500;">${item.description || "—"}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">${item.qty}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;">${fmtCurr(item.rate)}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">${item.taxPercent || 0}%</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;font-weight:500;">${fmtCurr(amt)}</td></tr>`;
    })
    .join("");

  const discHtml =
    discount > 0
      ? `<div style="display:flex;justify-content:space-between;font-size:12px;padding:4px 0;"><span>Discount</span><span style="color:#ef4444;">-${fmtCurr(discount)}</span></div>`
      : "";

  return `<div style="font-family:Inter,system-ui,sans-serif;padding:20px 0;">
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;">
<div><div style="width:48px;height:48px;background:#4f46e5;border-radius:8px;display:flex;align-items:center;justify-content:center;margin-bottom:8px;"><span style="color:white;font-weight:bold;font-size:14px;">${company.name.slice(0, 2).toUpperCase()}</span></div><h2 style="font-size:18px;font-weight:bold;margin:0 0 4px;">${company.name}</h2><p style="font-size:12px;color:#6b7280;margin:0;">${company.address}</p><p style="font-size:12px;color:#6b7280;margin:0;">${company.phone} | ${company.email}</p><p style="font-size:11px;color:#9ca3af;margin:4px 0 0;">GSTIN: ${company.gstin}</p></div>
<div style="text-align:right;"><h1 style="font-size:24px;font-weight:bold;margin:0;letter-spacing:0.05em;">QUOTATION</h1><p style="font-size:18px;font-weight:600;color:#4f46e5;margin:4px 0 0;">${quoteNo}</p><p style="font-size:12px;margin:12px 0 0;">Date: ${fmtDate(date)}</p><p style="font-size:12px;margin:2px 0 0;">Valid Until: ${fmtDate(validUntil)}</p></div></div>
<hr style="border:0;border-top:2px solid #e5e7eb;margin:16px 0;" />
<div style="margin-bottom:16px;"><p style="font-size:11px;font-weight:600;color:#9ca3af;text-transform:uppercase;margin:0 0 4px;">Bill To</p><p style="font-size:16px;font-weight:600;margin:0;">${leadName || "—"}</p><p style="font-size:13px;color:#6b7280;margin:0;">${leadCompany || ""}</p></div>
<table style="width:100%;border-collapse:collapse;margin-bottom:16px;"><thead><tr style="background:#f3f4f6;"><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:left;">#</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:left;">Description</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">Qty</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;">Rate</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">Tax</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;">Amount</th></tr></thead><tbody>${itemsHtml}</tbody></table>
<div style="display:flex;justify-content:flex-end;margin-bottom:16px;"><div style="width:240px;"><div style="display:flex;justify-content:space-between;font-size:12px;padding:4px 0;"><span>Subtotal</span><span>${fmtCurr(subtotal)}</span></div>${discHtml}<div style="display:flex;justify-content:space-between;font-size:12px;padding:4px 0;"><span>Tax</span><span>${fmtCurr(taxTotal)}</span></div><hr style="border:0;border-top:1px solid #e5e7eb;margin:4px 0;" /><div style="display:flex;justify-content:space-between;font-size:15px;font-weight:bold;padding:4px 0;color:#4f46e5;"><span>Total</span><span>${fmtCurr(total)}</span></div></div></div>
${terms ? `<div style="border-top:2px solid #e5e7eb;padding-top:12px;margin-bottom:16px;"><p style="font-size:11px;font-weight:600;color:#9ca3af;text-transform:uppercase;margin:0 0 4px;">Terms &amp; Conditions</p><p style="font-size:11px;white-space:pre-line;margin:0;">${terms.replace(/\n/g, "<br>")}</p></div>` : ""}
<div style="border-top:2px solid #e5e7eb;padding-top:12px;display:flex;justify-content:space-between;"><div><p style="font-size:12px;font-weight:600;margin:0;">Prepared by</p><p style="font-size:12px;margin:2px 0 0;">${agent || "Unassigned"}</p></div><div style="text-align:right;"><p style="font-size:11px;margin:0;">Authorized Signatory</p></div></div>
<p style="text-align:center;font-size:11px;color:#9ca3af;margin-top:24px;">Thank you for your business &mdash; ${company.name}</p></div>`;
}